/**
 * Cadastros do módulo Pedidos — conciliação (F2c). Junta grafias diferentes do
 * mesmo cliente/produto/transportadora ("Netzch" e "Netzsch"). O sistema só SUGERE;
 * quem decide é o usuário. A grafia juntada vira apelido: digitar ou reimportar
 * "Netzch" cai sempre em Netzsch.
 */
import { createEffect, createResource, createSignal, For, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { ConfirmDialog } from '@ui/components/ConfirmDialog'
import { Dialog } from '@ui/components/Dialog'
import { Input } from '@ui/components/Input'
import { Tabs } from '@ui/components/Tabs'
import type { CadastroPedidos, ParParecido, TipoCadastroPedidos } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'

const NOMES: Record<TipoCadastroPedidos, { plural: string; uso: string }> = {
  cliente: { plural: 'Clientes', uso: 'pedido(s)' },
  produto: { plural: 'Produtos', uso: 'item(ns)' },
  transportadora: { plural: 'Transportadoras', uso: 'pedido(s)' },
}

type Juncao = { origem: { id: number; nome: string; usos: number }; destino: { id: number; nome: string; usos: number } }

export function Cadastros(props: { podeEditar: boolean }) {
  const [tipo, setTipo] = createSignal<TipoCadastroPedidos>('cliente')
  const [tick, setTick] = createSignal(0)
  const [busca, setBusca] = createSignal('')
  const [ignorados, setIgnorados] = createSignal<Set<string>>(new Set())
  const [juntar, setJuntar] = createSignal<Juncao | null>(null)
  const [escolherDestino, setEscolherDestino] = createSignal<CadastroPedidos | null>(null)
  const [renomear, setRenomear] = createSignal<CadastroPedidos | null>(null)
  const [aviso, setAviso] = createSignal<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)
  const [pendente, setPendente] = createSignal(false)

  const chave = () => ({ tipo: tipo(), v: versaoDados(), t: tick() })
  const [pares] = createResource(chave, ({ tipo }) => api.pedidos.parecidos(tipo))
  const [lista] = createResource(() => ({ ...chave(), b: busca() }), ({ tipo, b }) => api.pedidos.cadastros(tipo, b || null))
  const parVisivel = (p: ParParecido) => !ignorados().has(`${tipo()}:${p.fica.id}:${p.sai.id}`)
  const paresVisiveis = () => (pares.latest ?? []).filter(parVisivel)

  const confirmarJuncao = async () => {
    const j = juntar()
    if (!j) return
    setPendente(true)
    try {
      const r = await api.pedidos.unir(tipo(), j.origem.id, j.destino.id)
      setAviso({ tipo: 'ok', texto: `"${j.origem.nome}" foi juntado em "${j.destino.nome}" (${r.alterados} ${NOMES[tipo()].uso}).` })
      setJuntar(null)
      setTick((t) => t + 1)
    } catch (e) {
      setAviso({ tipo: 'erro', texto: mensagemErro(e) })
    } finally {
      setPendente(false)
    }
  }

  return (
    <div class="h-full overflow-auto">
      <div class="max-w-4xl px-4 sm:px-6 py-5 flex flex-col gap-6">
        <div class="flex flex-wrap items-center gap-3">
          <Tabs
            aria-label="Tipo de cadastro"
            size="sm"
            value={tipo()}
            onChange={(t) => (setTipo(t), setBusca(''), setAviso(null))}
            options={(['cliente', 'produto', 'transportadora'] as const).map((t) => ({ value: t, label: NOMES[t].plural }))}
          />
          <p class="text-xs text-ink-subtle">
            Juntar une duas grafias do mesmo cadastro. A grafia que sai continua valendo como apelido, inclusive ao reimportar a planilha.
          </p>
        </div>

        <Show when={aviso()}>
          {(a) => (
            <p class="text-sm" classList={{ 'text-success': a().tipo === 'ok', 'text-failure': a().tipo === 'erro' }} role="status">
              {a().texto}
            </p>
          )}
        </Show>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-ink">
            Parecem o mesmo <span class="text-ink-subtle font-normal">· {paresVisiveis().length} sugestão(ões)</span>
          </h2>
          <Show
            when={paresVisiveis().length > 0}
            fallback={<p class="text-sm text-ink-placeholder">{pares.loading ? 'Procurando…' : 'Nenhuma sugestão.'}</p>}
          >
            <ul class="rounded-xl border border-edge bg-panel divide-y divide-edge-muted">
              <For each={paresVisiveis()}>
                {(p) => (
                  <li class="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                    <span class="text-ink font-medium">{p.fica.nome}</span>
                    <span class="text-ink-subtle text-xs">{p.fica.usos}</span>
                    <span class="text-ink-placeholder">·</span>
                    <span class="text-ink font-medium">{p.sai.nome}</span>
                    <span class="text-ink-subtle text-xs">{p.sai.usos}</span>
                    <span class="text-xs text-ink-placeholder">({p.motivo})</span>
                    <Show when={props.podeEditar}>
                      <span class="ml-auto flex flex-wrap gap-1.5">
                        <Button size="sm" variant="secondary" onClick={() => setJuntar({ origem: p.sai, destino: p.fica })}>
                          Juntar em "{p.fica.nome}"
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setJuntar({ origem: p.fica, destino: p.sai })}>
                          em "{p.sai.nome}"
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Esconder esta sugestão"
                          onClick={() => setIgnorados(new Set([...ignorados(), `${tipo()}:${p.fica.id}:${p.sai.id}`]))}
                        >
                          Não são o mesmo
                        </Button>
                      </span>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </section>

        <section class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-3">
            <h2 class="text-sm font-medium text-ink mr-auto">
              Todos os {NOMES[tipo()].plural.toLowerCase()} <span class="text-ink-subtle font-normal">· {(lista.latest ?? []).length}</span>
            </h2>
            <Input class="w-64 max-w-full" placeholder="Buscar" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
          </div>
          <div class="rounded-xl border border-edge bg-panel overflow-auto">
            <table class="w-full text-sm">
              <thead class="text-left text-ink-subtle border-b border-edge">
                <tr>
                  <th class="font-medium px-4 py-2">Nome</th>
                  <th class="font-medium px-4 py-2 text-right">Usos</th>
                  <th class="font-medium px-4 py-2 hidden md:table-cell">Outras grafias (apelidos)</th>
                  <th class="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                <For each={lista.latest ?? []}>
                  {(c) => (
                    <tr class="border-b border-edge-muted last:border-0 hover:bg-hover">
                      <td class="px-4 py-2 text-ink">{c.nome}</td>
                      <td class="px-4 py-2 text-right tabular-nums text-ink-muted">{c.usos}</td>
                      <td class="px-4 py-2 text-xs text-ink-subtle hidden md:table-cell">{c.apelidos.join(' · ')}</td>
                      <td class="px-4 py-1.5 text-right whitespace-nowrap">
                        <Show when={props.podeEditar}>
                          <Button size="sm" variant="ghost" onClick={() => setRenomear(c)}>Renomear</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEscolherDestino(c)}>Juntar em…</Button>
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={juntar() !== null}
        onOpenChange={(o) => !o && setJuntar(null)}
        title="Juntar cadastros?"
        body={
          juntar()
            ? `"${juntar()!.origem.nome}" (${juntar()!.origem.usos} ${NOMES[tipo()].uso}) passa a ser "${juntar()!.destino.nome}". ` +
              `A grafia "${juntar()!.origem.nome}" continua valendo como apelido.`
            : ''
        }
        confirmLabel="Juntar"
        pending={pendente()}
        onConfirm={() => void confirmarJuncao()}
      />

      <EscolherDestino
        tipo={tipo()}
        origem={escolherDestino()}
        aoFechar={() => setEscolherDestino(null)}
        aoEscolher={(d) => {
          const o = escolherDestino()!
          setEscolherDestino(null)
          setJuntar({ origem: o, destino: d })
        }}
      />

      <Renomear
        tipo={tipo()}
        cadastro={renomear()}
        aoFechar={() => setRenomear(null)}
        aoSalvar={(texto) => (setAviso({ tipo: 'ok', texto }), setTick((t) => t + 1))}
      />
    </div>
  )
}

function EscolherDestino(props: {
  tipo: TipoCadastroPedidos
  origem: CadastroPedidos | null
  aoFechar: () => void
  aoEscolher: (d: CadastroPedidos) => void
}) {
  const [busca, setBusca] = createSignal('')
  const [opcoes] = createResource(
    () => (props.origem ? { t: props.tipo, b: busca() } : false),
    ({ t, b }) => api.pedidos.cadastros(t, b || null),
  )
  return (
    <Dialog open={props.origem !== null} onOpenChange={(o) => !o && (setBusca(''), props.aoFechar())} class="w-[min(480px,calc(100vw-32px))]">
      <div class="p-6 flex flex-col gap-3">
        <h2 class="text-lg font-semibold text-ink">Juntar "{props.origem?.nome}" em…</h2>
        <Input placeholder="Buscar o cadastro que fica" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
        <ul class="max-h-72 overflow-auto rounded-lg border border-edge divide-y divide-edge-muted">
          <For each={(opcoes.latest ?? []).filter((c) => c.id !== props.origem?.id).slice(0, 50)}>
            {(c) => (
              <li>
                <button type="button" class="w-full text-left px-3 py-2 text-sm hover:bg-hover flex gap-2" onClick={() => props.aoEscolher(c)}>
                  <span class="text-ink">{c.nome}</span>
                  <span class="ml-auto text-xs text-ink-subtle tabular-nums">{c.usos}</span>
                </button>
              </li>
            )}
          </For>
        </ul>
        <div class="flex justify-end">
          <Button variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
        </div>
      </div>
    </Dialog>
  )
}

function Renomear(props: { tipo: TipoCadastroPedidos; cadastro: CadastroPedidos | null; aoFechar: () => void; aoSalvar: (aviso: string) => void }) {
  const [nome, setNome] = createSignal('')
  const [erro, setErro] = createSignal<string | null>(null)
  createEffect(on(() => props.cadastro, (c) => c && (setNome(c.nome), setErro(null))))
  const salvar = async (e: SubmitEvent) => {
    e.preventDefault()
    const c = props.cadastro!
    try {
      await api.pedidos.renomear(props.tipo, c.id, nome())
      props.aoSalvar(`"${c.nome}" agora se chama "${nome().trim()}".`)
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    }
  }
  return (
    <Dialog open={props.cadastro !== null} onOpenChange={(o) => !o && props.aoFechar()} class="w-[min(420px,calc(100vw-32px))]">
      <form onSubmit={salvar} class="p-6 flex flex-col gap-3">
        <h2 class="text-lg font-semibold text-ink">Renomear</h2>
        <Input value={nome()} onInput={(e) => setNome(e.currentTarget.value)} />
        <p class="text-xs text-ink-subtle">O nome antigo continua valendo como apelido. Nos pedidos, o nome é corrigido.</p>
        <Show when={erro()}>
          <p class="text-sm text-failure" role="alert">{erro()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent">Salvar</Button>
        </div>
      </form>
    </Dialog>
  )
}
